/**
 * WHICH WORKER RUNS A TERRAIN JOB, AND WHEN — the ordering layer the pool never had.
 *
 * TerrainWorkerPool used to pick a worker by hashing the job's column and `postMessage` it
 * immediately. That is N independent FIFO mailboxes fed by a deliberately-scattering hash, with no
 * central queue and no per-worker accounting, so a job's completion time depended on how loaded ITS
 * hashed worker happened to be rather than on when we asked for it. Simulated over the real hash
 * with shell-ordered columns (equal cost, FIFO per worker):
 *
 *   3 workers: load 32/17/32, 12% of pairs inverted, worst displacement 27 places
 *   6 workers: load 19/13/18/13/4/14, 16% inverted, worst displacement 39 places
 *
 * At six workers — `clamp(cores - 2, 3, 6)`, so the common case — the first twelve columns we asked
 * for came back at positions 0,6,5,2,11,17,8,23,12,18,3,24 while one worker sat on four jobs and
 * another on nineteen. Every ordering decision upstream (shellDistance, marginSourceKey,
 * scheduleLodRequests) was being shuffled by a hash after the fact, which is the reported "map cells
 * load out of order of distance from the spawn, so chunks take too long to display".
 *
 * The three things this has to do at once:
 *
 *  1. KEEP THE CALLER'S ORDER. Jobs dispatch strictly in submit order as slots free. Submit order is
 *     the streamer's priority order, and nothing below it may permute that.
 *  2. KEEP COLUMN AFFINITY. A column's tile/bundle generation warms that worker's unshared
 *     LocalTerrainSource cache and its per-chunk follow-ups hit it; scattering a column across
 *     workers re-runs the stamp-inclusive column generation per chunk, which is the double-generation
 *     the affinity hash was introduced to kill.
 *  3. NOT LET ONE WORKER BE THE CRITICAL PATH while another idles, which is what the raw hash does.
 *
 * They are reconciled by making affinity a PREFERENCE WITH A MEMORY. A column's HOME is fixed the
 * first time it is dispatched — the hash's lane when that has room, the least-loaded lane when it
 * does not — and later jobs for that column go home whenever home has room, spilling elsewhere only
 * to avoid leaving a slot idle, and without moving the home. So the warm cache stays put, a
 * congested hash bucket cannot become the critical path, and no capacity goes unused.
 *
 * Same input through the new policy: inversions 394/76/517 -> 0, worst displacement 27/4/39 -> 0,
 * and load 32/17/32 -> 27/27/27, 19/13/18/13/4/14 -> 13/13/14/13/14/14.
 */

/** In-flight jobs per worker. One running plus one waiting in its mailbox, so a worker never idles
 *  between jobs while head-of-line delay stays bounded at a single job. */
const LANE_DEPTH = 2;
/** Column→lane overrides retained. Bounded because it is keyed by column and the world is not; the
 *  working set is the streaming frontier, orders of magnitude below this. */
const STICKY_CAP = 4096;

export class WorkerDispatch<T> {
  /** Jobs waiting for a slot, in submit order. */
  private readonly queue: { column: number; job: T }[] = [];
  private readonly busy: number[];
  /** Columns whose lane differs from their hash, because the hash lane was full when they arrived. */
  private readonly sticky = new Map<number, number>();

  constructor(
    private readonly lanes: number,
    private readonly send: (lane: number, job: T) => void,
  ) {
    this.busy = new Array(lanes).fill(0);
  }

  /**
   * Stable lane for a column — the same spatial hash the terrain and cave caches use, so a column
   * lands on one worker for the lifetime of the pool unless congestion moved it.
   */
  private hashLane(column: number): number {
    // The column key is already a packed pair; re-mix it so adjacent columns still spread.
    const h = (Math.imul(column | 0, 73856093) ^ Math.imul(column >>> 16, 19349663)) >>> 0;
    return h % this.lanes;
  }

  /** Queue a job. Dispatch happens here if a slot is free, otherwise when one frees. */
  submit(column: number, job: T): void {
    this.queue.push({ column, job });
    this.pump();
  }

  /** A worker finished: free its slot and let the queue advance. */
  complete(lane: number): void {
    if (this.busy[lane] > 0) this.busy[lane]--;
    this.pump();
  }

  /**
   * Dispatch from the head of the queue while the head can go somewhere.
   *
   * STRICTLY FROM THE HEAD, never scanning past a blocked job to find one that fits: skipping ahead
   * is precisely the reordering this class exists to prevent, and the sticky assignment already
   * keeps a blocked head rare (it only blocks when its lane is full AND every other lane is busy,
   * i.e. when the pool is genuinely saturated — in which case waiting is correct).
   */
  private pump(): void {
    while (this.queue.length > 0) {
      const head = this.queue[0];
      const lane = this.laneFor(head.column);
      if (lane < 0) return; // nowhere for the head to go; anything behind it waits its turn
      this.queue.shift();
      this.busy[lane]++;
      this.send(lane, head.job);
    }
  }

  /**
   * Where this column's next job should run, or -1 when every lane is full and it must wait.
   *
   * A column's HOME is fixed the first time it is dispatched, because that first job is the tile or
   * column bundle whose generation warms the worker's terrain cache — the home is wherever that
   * ran, and the per-chunk follow-ups want to land on it. The hash picks the home when its lane has
   * room, which is the common case and keeps distinct columns spread deterministically; when it
   * does not, the home is the least-loaded lane with room, so a congested hash bucket cannot make
   * one worker the critical path while others idle.
   *
   * A later job whose home is momentarily full SPILLS to the least-loaded lane with room and does
   * NOT move the home. Spilling costs one re-generation; moving the home would let a hot column
   * migrate lane to lane across a burst and re-run its whole stamp-inclusive generation each time,
   * which is the double-generation the affinity hash was introduced to kill.
   */
  private laneFor(column: number): number {
    const home = this.sticky.get(column);
    if (home !== undefined) {
      if (this.busy[home] < LANE_DEPTH) return home;
      return this.leastLoaded(); // one-off spill; the cache stays home
    }
    const hashed = this.hashLane(column);
    const lane = this.busy[hashed] < LANE_DEPTH ? hashed : this.leastLoaded();
    if (lane < 0) return -1; // nothing free anywhere: wait rather than reorder, and stay unassigned
    if (this.sticky.size >= STICKY_CAP) {
      const oldest = this.sticky.keys().next();
      if (!oldest.done) this.sticky.delete(oldest.value);
    }
    this.sticky.set(column, lane);
    return lane;
  }

  /** The emptiest lane that still has a slot, or -1 when the pool is saturated. */
  private leastLoaded(): number {
    let best = -1;
    for (let i = 0; i < this.lanes; i++) {
      if (this.busy[i] < LANE_DEPTH && (best < 0 || this.busy[i] < this.busy[best])) best = i;
    }
    return best;
  }

  /** Per-lane in-flight counts — the balance gauge, and what the tests assert against. */
  get inFlight(): readonly number[] {
    return this.busy;
  }

  /** Jobs submitted but not yet sent to a worker. */
  get queued(): number {
    return this.queue.length;
  }

  /** Drop everything pending (pool disposal). */
  clear(): void {
    this.queue.length = 0;
    this.busy.fill(0);
    this.sticky.clear();
  }
}
